/**
 * investigationFlows.ts
 * 土地法規等調査・人口プロファイル・空間クエリ関連メソッド
 * App.vue から切り出し
 */

import * as turf from '@turf/turf'
import { startAreaAnalysisFreehand } from '@/js/areaAnalyzer'

type VM = any

export async function startPopulationProfilePolygonFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const { startAreaAnalysisPolygon } = await import('@/js/areaAnalyzer')
  const polygon = await startAreaAnalysisPolygon(map)
  if (polygon) {
    vm.$store.commit('addFWInstance', {
      type: 'super-population',
      data: { polygonData: polygon, analysisMode: 'polygon' },
    })
  }
}

export function openFsInvestigationModeDialog(vm: VM) {
  vm.showFsInvestigationModeDialog = true
}

export function cancelFsInvestigationModeDialog(vm: VM) {
  vm.showFsInvestigationModeDialog = false
}

export async function selectFsInvestigationMode(vm: VM, mode: string) {
  vm.showFsInvestigationModeDialog = false
  if (mode === 'point') {
    vm.$store.commit('addFWInstance', {
      type: 'fs-investigation',
      data: { analysisMode: 'point' }
    })
    return
  }
  if (mode === 'freehand') {
    await startFsInvestigationFreehandFlow(vm)
    return
  }
  if (mode === 'circle') {
    await startFsInvestigationCircleFlow(vm)
    return
  }
  if (mode === 'polygon') {
    await startFsInvestigationPolygonFlow(vm)
    return
  }
}

async function checkFsAreaLimit(vm: VM, polygon: GeoJSON.Feature) {
  const areaM2 = turf.area(polygon)
  const areaHa = areaM2 / 10000
  const MAX_AREA_HA = 40
  if (areaHa > MAX_AREA_HA) {
    vm.fsAreaTooLargeValue = areaHa.toFixed(2)
    vm.showFsAreaTooLargeDialog = true
    return false
  }
  return true
}

export async function startFsInvestigationFreehandFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const polygon = await startAreaAnalysisFreehand(map)
  if (polygon) {
    if (!(await checkFsAreaLimit(vm, polygon))) return
    vm.$store.commit('addFWInstance', {
      type: 'fs-investigation',
      data: { polygonData: polygon, analysisMode: 'freehand' },
    })
  }
}

export async function startFsInvestigationCircleFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const { startAreaAnalysisCircle } = await import('@/js/areaAnalyzer')
  const polygon = await startAreaAnalysisCircle(map)
  if (polygon) {
    if (!(await checkFsAreaLimit(vm, polygon))) return
    vm.$store.commit('addFWInstance', {
      type: 'fs-investigation',
      data: { polygonData: polygon, analysisMode: 'circle' },
    })
  }
}

export async function startFsInvestigationPolygonFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const { startAreaAnalysisPolygon } = await import('@/js/areaAnalyzer')
  const polygon = await startAreaAnalysisPolygon(map)
  if (polygon) {
    if (!(await checkFsAreaLimit(vm, polygon))) return
    vm.$store.commit('addFWInstance', {
      type: 'fs-investigation',
      data: { polygonData: polygon, analysisMode: 'polygon' },
    })
  }
}

export function openSuperCrossSectionWizard(vm: VM) {
  vm.$store.dispatch('toggleFloatingWindow', 'super-cross-section-window')
}

export function openSpatialQueryModeDialog(vm: VM) {
  vm.spatialQueryPolygonData = null
  vm.$store.dispatch('toggleFloatingWindow', 'spatial-query-window')
}

export function beginAreaAnalysisPointSelection(vm: VM) {
  const map = vm.$store.state.map01
  if (!map || vm.areaAnalysisPointSelectActive) return

  map.getCanvas().classList.remove('is-area-analysis-freehand')

  vm.areaAnalysisPointSelectActive = true

  const canvas = map.getCanvas()
  const prevCursor = canvas.style.cursor
  canvas.style.cursor = 'default'
  canvas.classList.add('is-area-analysis-point-select')
  const mapContainer = document.getElementById('map01')
  if (mapContainer) {
    mapContainer.classList.add('ai-area-analysis-point-cursor')
  }

  map.once('click', (e: any) => {
    vm.areaAnalysisPointSelectActive = false
    canvas.style.cursor = prevCursor
    canvas.classList.remove('is-area-analysis-point-select')
    if (mapContainer) {
      mapContainer.classList.remove('ai-area-analysis-point-cursor')
    }
    openAreaAnalysisPoint(vm, e.lngLat)
  })
}

export function openAreaAnalysisPoint(vm: VM, lngLat: { lng: number; lat: number }) {
  if (!lngLat) return
  const pointFeature = {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [lngLat.lng, lngLat.lat],
    },
    properties: {},
  }
  vm.$store.commit('addShareFeature', {
    aiType: 'risk',
    geometry: pointFeature.geometry,
    properties: {}
  })
  const openSimulation = vm.pendingOpenSimulationDialog
  if (openSimulation) {
    vm.pendingOpenSimulationDialog = false
  }
  vm.$store.commit('addFWInstance', {
    type: 'area-analysis',
    data: { pointData: pointFeature, analysisMode: 'point', openSimulationDialog: openSimulation },
  })
}
